import { useSessionStorage } from "@vueuse/core"
import type { Session, SessionWithToken } from "~/types/api/auth/login"
import type { Feature } from "~/utils/features"

export type LoginTwoFactorChallenge = {
  two_factor: true
  login_challenge: string
}

export function isTwoFactorChallenge(
  result: SessionWithToken | LoginTwoFactorChallenge,
): result is LoginTwoFactorChallenge {
  return "two_factor" in result && result.two_factor
}

export const useAuth = () => {
  /** Contains information about logged in user and workspace */
  const session = useState<Session | undefined>()
  const twoFactorLoginChallenge = useSessionStorage<string | undefined>(
    "two-factor-login-challenge",
    undefined,
  )

  const featureOverrides = useState<Partial<Record<Feature, boolean>>>(
    "featureOverrides",
    () => ({}),
  )

  /** Token cookie, set upon login */
  const token = useCookie<string | undefined>("token")

  const workspaceId = useCookie<string | undefined>("workspace_id")

  const isImpersonating = useCookie<true | undefined>("impersonating")
  const impersonatorId = useCookie<string | undefined>("impersonator_id")

  const { $apiCsrfOnly, $api } = useNuxtApp()
  const runtimeConfig = useRuntimeConfig()

  function setSession(result: SessionWithToken) {
    session.value = result
    token.value = result.token
    workspaceId.value = result.workspace
  }

  function setTwoFactorLoginChallenge(loginChallenge: string) {
    twoFactorLoginChallenge.value = loginChallenge
  }

  function clearTwoFactorLoginChallenge() {
    twoFactorLoginChallenge.value = undefined
  }

  /** Logs in with provided credentials and sets `session` state and `token`
   * cookie if successful */
  async function login(
    email: string,
    password: string,
  ): Promise<SessionWithToken | LoginTwoFactorChallenge> {
    const result = await $apiCsrfOnly<
      SessionWithToken | LoginTwoFactorChallenge
    >("/admin/v1/auth/login", {
      baseURL: runtimeConfig.public.apiBase,
      method: "post",
      body: { email, password },
      suppressedStatusCodes: [401, 404],
    })

    if (isTwoFactorChallenge(result)) {
      setTwoFactorLoginChallenge(result.login_challenge)
      return result
    }

    clearTwoFactorLoginChallenge()
    setSession(result)
    return result
  }

  async function completeTwoFactorChallenge(
    body: { code: string } | { recovery_code: string },
  ): Promise<SessionWithToken> {
    if (!twoFactorLoginChallenge.value) {
      throw new Error("Missing two-factor login challenge")
    }

    const result = await $apiCsrfOnly<SessionWithToken>(
      "/admin/v1/auth/two-factor/challenge",
      {
        baseURL: runtimeConfig.public.apiBase,
        method: "post",
        body: {
          login_challenge: twoFactorLoginChallenge.value,
          ...body,
        },
        suppressedStatusCodes: [401, 422],
      },
    )

    clearTwoFactorLoginChallenge()
    setSession(result)
    return result
  }

  async function getSession(): Promise<Session | undefined> {
    if (!token.value || !workspaceId.value) {
      return
    }
    const result = await $apiCsrfOnly<Session>("/admin/v1/session", {
      baseURL: runtimeConfig.public.apiBase,
      headers: {
        Authorization: `Bearer ${token.value}`,
        "X-Workspace": workspaceId.value,
      },
    })
    session.value = result
    workspaceId.value = result.workspace
    return result
  }

  async function logout() {
    if (!token.value) {
      return
    }
    try {
      await $api("/admin/v1/auth/logout", {
        method: "post",
        suppressedStatusCodes: [401, 404, 419],
      })
    } finally {
      clearAuthState()
    }
  }

  const clearAuthState = () => {
    token.value = undefined
    workspaceId.value = undefined
    session.value = undefined
    isImpersonating.value = undefined
    impersonatorId.value = undefined
    clearTwoFactorLoginChallenge()
  }

  const hasFeature = (featureName: Feature): boolean => {
    const override = featureOverrides.value[featureName]
    if (override != undefined) {
      return override
    }
    return session.value?.features.includes(featureName) ?? false
  }

  const hasSessionFeature = (featureName: Feature): boolean => {
    return session.value?.features.includes(featureName) ?? false
  }

  const setFeatureOverride = (featureName: Feature, enabled: boolean) => {
    // Matching the session value means there is no override to keep.
    if (enabled == hasSessionFeature(featureName)) {
      const { [featureName]: _removedOverride, ...overrides } =
        featureOverrides.value
      featureOverrides.value = overrides
      return
    }

    featureOverrides.value = {
      ...featureOverrides.value,
      [featureName]: enabled,
    }
  }

  const clearFeatureOverrides = () => {
    featureOverrides.value = {}
  }

  const userRole = computed(() => session.value?.user.role)

  return {
    clearFeatureOverrides,
    featureOverrides,
    hasFeature,
    hasSessionFeature,
    login,
    completeTwoFactorChallenge,
    clearTwoFactorLoginChallenge,
    twoFactorLoginChallenge,
    setFeatureOverride,
    token,
    session,
    logout,
    getSession,
    workspaceId,
    userRole,
    isImpersonating,
    impersonatorId,
  }
}
